/**
 * Admin Panel for Fixtures Management
 * Generate fixtures, configure points, manage matches.
 * Points, tie-breakers and the knockout format are set in the rulebook (the
 * only place). With a rulebook this panel shows them read-only; without one
 * it shows the points in use and sends the organiser to set up a rulebook.
 */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import { derivePhaseBadge } from './phaseBadge';
import { KNOCKOUT_ROUND_TYPES } from './rounds';
import HowPointsWork from '../rulebook/HowPointsWork';

export default function FixturesAdminPanel({ eventId, event, fixtures, teams, pointConfig, standingsRules, onRefresh }) {
  const managed = !!standingsRules?.managed;
  const rbKnockout = managed ? standingsRules.knockout : null;
  const knockoutFromRulebook = !!rbKnockout && rbKnockout.format !== 'custom';
  const knockoutLine = (standingsRules?.lines || []).find((l) => l.startsWith('Knockouts:'));
  const [activeTab, setActiveTab] = useState('generate');
  const [, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  
  // Generate fixtures form state
  const [generateForm, setGenerateForm] = useState({
    structure_type: 'league',
    num_groups: 2,
    teams_qualify_per_group: 2,
    knockout_stages: ['semi_final', 'final'],
    start_date: '',
    matches_per_day: 2,
    venue_name: event?.venue?.name || '',
    turfs: ['Turf 1'],
    match_duration_minutes: 90,
    break_between_matches_minutes: 10,
    generate_all_knockout_rounds: true,
  });

  const [savedKnockoutStages, setSavedKnockoutStages] = useState([]);

  // Prefill from saved cricket_event_config (knockout_stages + tournament_structure)
  // so the choices made during event creation are respected as defaults.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(`/v1/admin/events/${eventId}/cricket`);
        if (cancelled) return;
        const cfg = res.data?.config || res.data || {};
        const structureKey = (cfg.tournament_structure_key || '').toLowerCase();
        const savedStages = Array.isArray(cfg.knockout_stages) ? cfg.knockout_stages : [];
        setSavedKnockoutStages(savedStages);
        let mappedStructure = null;
        if (structureKey === 'round_robin') {
          mappedStructure = savedStages.length > 0 ? 'league_knockout' : 'league';
        } else if (structureKey === 'knockout') {
          mappedStructure = 'knockout';
        } else if (structureKey === 'groups') {
          mappedStructure = savedStages.length > 0 ? 'groups_knockout' : 'groups';
        } else if (structureKey === 'groups_knockout' || structureKey === 'league_knockout') {
          mappedStructure = structureKey;
        }
        setGenerateForm(prev => ({
          ...prev,
          ...(mappedStructure ? { structure_type: mappedStructure } : {}),
          ...(savedStages.length > 0 ? { knockout_stages: savedStages } : {}),
        }));
      } catch (e) {
        // Non-admin or cricket config unavailable — keep hardcoded defaults
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  // Manual fixture form
  const [manualForm, setManualForm] = useState({
    round_type: 'league',
    round_number: 1,
    group_name: '',
    team_a_id: '',
    team_b_id: '',
    scheduled_at: '',
    venue_name: event?.venue?.name || '',
    turf_name: 'Turf 1',
  });

  const [turfInput, setTurfInput] = useState('');

  const handleAddTurf = () => {
    if (turfInput.trim() && !generateForm.turfs.includes(turfInput.trim())) {
      setGenerateForm(prev => ({
        ...prev,
        turfs: [...prev.turfs, turfInput.trim()]
      }));
      setTurfInput('');
    }
  };

  const handleRemoveTurf = (turf) => {
    setGenerateForm(prev => ({
      ...prev,
      turfs: prev.turfs.filter(t => t !== turf)
    }));
  };

  const handleGenerateFixtures = async () => {
    if (teams.length < 2) {
      toast.error('At least 2 teams required to generate fixtures');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...generateForm,
        start_date: generateForm.start_date ? new Date(generateForm.start_date).toISOString() : null,
        knockout_stages: generateForm.structure_type.includes('knockout') ? generateForm.knockout_stages : null,
        num_groups: ['groups', 'groups_knockout'].includes(generateForm.structure_type) ? generateForm.num_groups : null,
      };

      const res = await axiosInstance.post(`/v1/events/${eventId}/fixtures/generate`, payload);
      toast.success(res.data.message);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate fixtures');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllFixtures = async () => {
    if (!window.confirm('Delete ALL fixtures and standings? This cannot be undone.')) return;

    setLoading(true);
    try {
      await axiosInstance.delete(`/v1/events/${eventId}/fixtures?confirm=true`);
      toast.success('All fixtures deleted');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete fixtures');
    } finally {
      setLoading(false);
    }
  };

  const handleResequenceMatchNumbers = async () => {
    if (!window.confirm('Renumber all fixtures into a clean 1..N sequence ordered by scheduled time? This only touches match numbers — teams, results, and schedules are untouched.')) return;

    setLoading(true);
    try {
      const res = await axiosInstance.post(`/v1/events/${eventId}/fixtures/resequence-match-numbers`);
      const { renumbered = 0, total = 0 } = res.data || {};
      toast.success(`Resequenced ${renumbered} of ${total} fixture${total === 1 ? '' : 's'}`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resequence match numbers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateManualFixture = async () => {
    if (!manualForm.team_a_id || !manualForm.team_b_id) {
      toast.error('Please select both teams');
      return;
    }
    if (manualForm.team_a_id === manualForm.team_b_id) {
      toast.error('Teams must be different');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...manualForm,
        scheduled_at: manualForm.scheduled_at ? new Date(manualForm.scheduled_at).toISOString() : null,
        group_name: manualForm.group_name || null,
      };

      await axiosInstance.post(`/v1/events/${eventId}/fixtures`, payload);
      toast.success('Fixture created');
      onRefresh();
      // Reset form
      setManualForm(prev => ({
        ...prev,
        team_a_id: '',
        team_b_id: '',
        scheduled_at: '',
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create fixture');
    } finally {
      setLoading(false);
    }
  };

  const ADMIN_TABS = [
    { key: 'generate', label: 'Auto Generate', icon: 'ph-magic-wand' },
    { key: 'manual', label: 'Add Match', icon: 'ph-plus-circle' },
    { key: 'points', label: 'Points', icon: 'ph-trophy' },
  ];

  const STRUCTURE_OPTIONS = [
    { value: 'league', label: 'League (Round Robin)' },
    { value: 'league_knockout', label: 'League + Knockout' },
    { value: 'groups', label: 'Groups Only' },
    { value: 'knockout', label: 'Knockout Only' },
    { value: 'groups_knockout', label: 'Groups + Knockout' },
  ];

  const KNOCKOUT_STAGES = [
    { value: 'round_of_16', label: 'Round of 16' },
    { value: 'quarter_final', label: 'Quarter Final' },
    { value: 'semi_final', label: 'Semi Final' },
    { value: 'third_place', label: 'Third Place' },
    { value: 'final', label: 'Final' },
  ];

  const ROUND_TYPES = [
    { value: 'league', label: 'League' },
    { value: 'group', label: 'Group Stage' },
    { value: 'quarter_final', label: 'Quarter Final' },
    { value: 'semi_final', label: 'Semi Final' },
    { value: 'third_place', label: 'Third Place' },
    { value: 'final', label: 'Final' },
  ];

  // Derived state for the knockout lifecycle buttons
  const hasLeagueOrGroup = fixtures.some(f => ['league', 'group'].includes(f.round_type));
  const hasKnockouts = fixtures.some(f => KNOCKOUT_ROUND_TYPES.includes(f.round_type));
  const canAddKnockouts = hasLeagueOrGroup && !hasKnockouts;

  // "Close League" gateway is available when:
  //   • League/group fixtures exist AND are ALL completed
  //   • Knockout fixtures exist (otherwise use "Add Knockouts" first)
  //   • The earliest knockout round still has TBD slots (otherwise already seeded)
  const allLeagueCompleted = hasLeagueOrGroup &&
    fixtures.filter(f => ['league', 'group'].includes(f.round_type))
            .every(f => ['completed', 'cancelled'].includes(f.match_status));
  // Not seeded yet: no knockout has both teams (works for brackets, playoffs
  // and cups alike; a three-team bracket's final holds only the top team)
  const knockoutFixtures = fixtures.filter(f => KNOCKOUT_ROUND_TYPES.includes(f.round_type));
  const knockoutsUnseeded = knockoutFixtures.every(f => !(f.team_a_id && f.team_b_id) && f.match_status === 'scheduled');
  const canCloseLeague = hasLeagueOrGroup && hasKnockouts && allLeagueCompleted && knockoutsUnseeded
    && rbKnockout?.format !== 'custom';

  const [showAddKnockoutsModal, setShowAddKnockoutsModal] = useState(false);
  const [addKnockoutsStages, setAddKnockoutsStages] = useState([]);
  useEffect(() => {
    // Seed the modal's stage selection from the event's saved knockout_stages
    if (savedKnockoutStages.length > 0) setAddKnockoutsStages(savedKnockoutStages);
    else setAddKnockoutsStages(['semi_final', 'final']);
  }, [savedKnockoutStages]);

  const handleAddKnockouts = async () => {
    if (!knockoutFromRulebook && addKnockoutsStages.length === 0) {
      toast.error('Pick at least one knockout stage');
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.post(
        `/v1/events/${eventId}/fixtures/knockouts/add-to-existing`,
        { knockout_stages: addKnockoutsStages }
      );
      toast.success(res.data?.message || 'Knockouts added');
      setShowAddKnockoutsModal(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add knockouts');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseLeague = async () => {
    const ok = window.confirm(
      'Close the league phase and seed the knockouts?\n\n'
      + '• The top teams from the final standings will be placed into the earliest knockout round.\n'
      + '• You will NOT be able to add more league matches that affect seeding after this.\n\n'
      + 'Proceed?'
    );
    if (!ok) return;
    setLoading(true);
    try {
      const res = await axiosInstance.post(`/v1/events/${eventId}/fixtures/league/close`);
      toast.success(res.data?.message || 'League closed & knockouts seeded');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close league');
    } finally {
      setLoading(false);
    }
  };

  // Phase status badge derivation (shared with public Fixtures tab)
  const phaseBadge = derivePhaseBadge(fixtures);

  return (
    <div className="bg-[#2B3A67] rounded-xl overflow-hidden" data-testid="fixtures-admin-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2.5">
            <h3 className="text-white font-bold flex items-center gap-2">
              <i className="ph-bold ph-gear-six"></i>
              Fixtures Management
            </h3>
            {phaseBadge && (
              <span
                className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg border ${phaseBadge.tone}`}
                data-testid="phase-status-badge"
              >
                <i className={`ph-bold ${phaseBadge.icon}`}></i>
                {phaseBadge.label}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {canCloseLeague && (
              <button
                onClick={handleCloseLeague}
                disabled={loading}
                className="text-xs text-amber-300 hover:text-amber-200 flex items-center gap-1 border border-amber-300/40 bg-amber-500/10 rounded-lg px-2.5 py-1"
                data-testid="close-league-btn"
                title="Close the league phase and seed the knockouts from final standings"
              >
                <i className="ph-bold ph-flag-checkered"></i>
                Close League & Seed Knockouts
              </button>
            )}
            {canAddKnockouts && (
              <button
                onClick={() => setShowAddKnockoutsModal(true)}
                disabled={loading}
                className="text-xs text-emerald-300 hover:text-emerald-200 flex items-center gap-1"
                data-testid="add-knockouts-btn"
                title="Add SF/Final (and any missing knockout stages) on top of the existing league"
              >
                <i className="ph-bold ph-tree-structure"></i>
                Add Knockouts
              </button>
            )}
            {fixtures.length > 0 && (
              <button
                onClick={handleResequenceMatchNumbers}
                disabled={loading}
                className="text-xs text-sky-300 hover:text-sky-200 flex items-center gap-1"
                data-testid="resequence-match-numbers-btn"
                title="Renumber all fixtures into a clean 1..N sequence (heals gaps left by deletions)"
              >
                <i className="ph-bold ph-sort-ascending"></i>
                Resequence #
              </button>
            )}
            {fixtures.length > 0 && (
              <button
                onClick={handleDeleteAllFixtures}
                disabled={loading}
                className="text-xs text-red-300 hover:text-red-200 flex items-center gap-1"
                data-testid="delete-all-fixtures-btn"
              >
                <i className="ph-bold ph-trash"></i>
                Delete All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/10">
        {ADMIN_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 px-3 py-2 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${
              activeTab === tab.key
                ? 'bg-white/10 text-white'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            <i className={`ph-bold ${tab.icon}`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Generate Tab */}
        {activeTab === 'generate' && (
          <div className="space-y-4">
            {fixtures.length > 0 && (
              <div className="bg-amber-500/20 text-amber-200 text-xs px-3 py-2 rounded-lg flex items-center gap-2">
                <i className="ph-bold ph-warning"></i>
                {fixtures.length} fixtures exist. Delete them first to regenerate.
              </div>
            )}

            {/* Config mismatch warning: saved knockout_stages would be dropped */}
            {savedKnockoutStages.length > 0 && !generateForm.structure_type.includes('knockout') && (
              <div className="bg-red-500/20 text-red-100 text-xs px-3 py-2 rounded-lg flex items-start gap-2" data-testid="knockout-mismatch-warning">
                <i className="ph-bold ph-warning-circle mt-0.5"></i>
                <div>
                  <strong>Heads up:</strong> During event creation you selected knockout stages
                  ({savedKnockoutStages.join(', ').replace(/_/g, ' ')}), but the currently-selected
                  structure <em>{STRUCTURE_OPTIONS.find(o => o.value === generateForm.structure_type)?.label}</em> will
                  NOT generate them. Pick <strong>League + Knockout</strong> (or a groups/knockout format) to keep them.
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white/60 block mb-1">Tournament Structure</label>
                <select
                  value={generateForm.structure_type}
                  onChange={e => setGenerateForm(prev => ({ ...prev, structure_type: e.target.value }))}
                  className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none"
                  data-testid="structure-type-select"
                >
                  {STRUCTURE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value} className="bg-[#2B3A67]">{opt.label}</option>
                  ))}
                </select>
              </div>

              {['groups', 'groups_knockout'].includes(generateForm.structure_type) && (
                <div>
                  <label className="text-xs text-white/60 block mb-1">Number of Groups</label>
                  <input
                    type="number"
                    min={2}
                    max={8}
                    value={generateForm.num_groups}
                    onChange={e => setGenerateForm(prev => ({ ...prev, num_groups: parseInt(e.target.value) }))}
                    className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none"
                  />
                </div>
              )}
            </div>

            {generateForm.structure_type.includes('knockout') && knockoutFromRulebook && (
              <div className="bg-white/10 rounded-lg p-3 text-xs text-white/85 flex items-start justify-between gap-3" data-testid="knockouts-from-rulebook">
                <span><span className="font-bold text-white">From the Rulebook.</span> {knockoutLine}</span>
                <a href="?phase=rules" onClick={(e) => { e.preventDefault(); setActiveTab('points'); }}
                  className="shrink-0 font-bold text-[#3AAFA9]">How points work</a>
              </div>
            )}

            {generateForm.structure_type.includes('knockout') && !knockoutFromRulebook && (
              <div>
                <label className="text-xs text-white/60 block mb-1">Knockout Stages</label>
                <div className="flex flex-wrap gap-2">
                  {KNOCKOUT_STAGES.map(stage => (
                    <label
                      key={stage.value}
                      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs cursor-pointer transition-colors ${
                        generateForm.knockout_stages.includes(stage.value)
                          ? 'bg-[#3AAFA9] text-white'
                          : 'bg-white/10 text-white/60 hover:bg-white/20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={generateForm.knockout_stages.includes(stage.value)}
                        onChange={e => {
                          if (e.target.checked) {
                            setGenerateForm(prev => ({
                              ...prev,
                              knockout_stages: [...prev.knockout_stages, stage.value]
                            }));
                          } else {
                            setGenerateForm(prev => ({
                              ...prev,
                              knockout_stages: prev.knockout_stages.filter(s => s !== stage.value)
                            }));
                          }
                        }}
                        className="hidden"
                      />
                      {stage.label}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <label className="text-xs text-white/60 block mb-1">Start Date</label>
                <input
                  type="datetime-local"
                  value={generateForm.start_date}
                  onChange={e => setGenerateForm(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-white/60 block mb-1">Matches per Day</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={generateForm.matches_per_day}
                  onChange={e => setGenerateForm(prev => ({ ...prev, matches_per_day: parseInt(e.target.value) }))}
                  className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none"
                />
              </div>
            </div>

            {/* Match Scheduling Settings */}
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <label className="text-xs text-white/60 block mb-1">Match Duration (mins)</label>
                <input
                  type="number"
                  min={30}
                  max={300}
                  value={generateForm.match_duration_minutes}
                  onChange={e => setGenerateForm(prev => ({ ...prev, match_duration_minutes: parseInt(e.target.value) }))}
                  className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none"
                  placeholder="90"
                />
              </div>
              <div>
                <label className="text-xs text-white/60 block mb-1">Break Between Matches (mins)</label>
                <input
                  type="number"
                  min={0}
                  max={60}
                  value={generateForm.break_between_matches_minutes}
                  onChange={e => setGenerateForm(prev => ({ ...prev, break_between_matches_minutes: parseInt(e.target.value) }))}
                  className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none"
                  placeholder="10"
                />
              </div>
            </div>

            {/* Generate All Knockout Rounds */}
            {(generateForm.structure_type === 'knockout' || (generateForm.structure_type === 'groups_knockout' && !knockoutFromRulebook)) && (
              <div className="flex items-center gap-2 bg-white/5 rounded-lg p-3">
                <input
                  type="checkbox"
                  id="generate_all_knockout"
                  checked={generateForm.generate_all_knockout_rounds}
                  onChange={e => setGenerateForm(prev => ({ ...prev, generate_all_knockout_rounds: e.target.checked }))}
                  className="w-4 h-4 rounded text-[#3AAFA9] focus:ring-[#3AAFA9] bg-white/10 border-white/20"
                />
                <label htmlFor="generate_all_knockout" className="text-xs text-white/80">
                  Generate all knockout rounds to Finals (with TBD teams)
                </label>
              </div>
            )}

            <div>
              <label className="text-xs text-white/60 block mb-1">Venue</label>
              <input
                type="text"
                value={generateForm.venue_name}
                onChange={e => setGenerateForm(prev => ({ ...prev, venue_name: e.target.value }))}
                placeholder="Main Stadium"
                className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none placeholder:text-white/30"
              />
            </div>

            <div>
              <label className="text-xs text-white/60 block mb-1">Turfs (for parallel matches)</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={turfInput}
                  onChange={e => setTurfInput(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleAddTurf()}
                  placeholder="Add turf name..."
                  className="flex-1 bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none placeholder:text-white/30"
                />
                <button
                  onClick={handleAddTurf}
                  className="px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20"
                >
                  <i className="ph-bold ph-plus"></i>
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {generateForm.turfs.map(turf => (
                  <span
                    key={turf}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-[#3AAFA9]/20 text-[#3AAFA9] rounded-lg text-xs"
                  >
                    {turf}
                    <button onClick={() => handleRemoveTurf(turf)} className="hover:text-red-400">
                      <i className="ph-bold ph-x text-[10px]"></i>
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <button
              onClick={handleGenerateFixtures}
              disabled={loading || teams.length < 2 || fixtures.length > 0}
              className="w-full py-2.5 bg-[#3AAFA9] text-white font-bold rounded-lg hover:bg-[#2D8A85] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="generate-fixtures-btn"
            >
              {loading ? (
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
              ) : (
                <>
                  <i className="ph-bold ph-magic-wand"></i>
                  Generate {teams.length} Team Fixtures
                </>
              )}
            </button>
          </div>
        )}

        {/* Manual Tab */}
        {activeTab === 'manual' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white/60 block mb-1">Round Type</label>
                <select
                  value={manualForm.round_type}
                  onChange={e => setManualForm(prev => ({ ...prev, round_type: e.target.value }))}
                  className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none"
                >
                  {ROUND_TYPES.map(opt => (
                    <option key={opt.value} value={opt.value} className="bg-[#2B3A67]">{opt.label}</option>
                  ))}
                </select>
              </div>
              {manualForm.round_type === 'group' && (
                <div>
                  <label className="text-xs text-white/60 block mb-1">Group</label>
                  <select
                    value={manualForm.group_name}
                    onChange={e => setManualForm(prev => ({ ...prev, group_name: e.target.value }))}
                    className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none"
                  >
                    <option value="" className="bg-[#2B3A67]">Select Group</option>
                    {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map(g => (
                      <option key={g} value={g} className="bg-[#2B3A67]">Group {g}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white/60 block mb-1">Team A</label>
                <select
                  value={manualForm.team_a_id}
                  onChange={e => setManualForm(prev => ({ ...prev, team_a_id: e.target.value }))}
                  className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none"
                  data-testid="team-a-select"
                >
                  <option value="" className="bg-[#2B3A67]">Select Team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id} className="bg-[#2B3A67]">{team.team_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-white/60 block mb-1">Team B</label>
                <select
                  value={manualForm.team_b_id}
                  onChange={e => setManualForm(prev => ({ ...prev, team_b_id: e.target.value }))}
                  className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none"
                  data-testid="team-b-select"
                >
                  <option value="" className="bg-[#2B3A67]">Select Team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id} className="bg-[#2B3A67]">{team.team_name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white/60 block mb-1">Scheduled Date/Time</label>
                <input
                  type="datetime-local"
                  value={manualForm.scheduled_at}
                  onChange={e => setManualForm(prev => ({ ...prev, scheduled_at: e.target.value }))}
                  className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs text-white/60 block mb-1">Turf</label>
                <input
                  type="text"
                  value={manualForm.turf_name}
                  onChange={e => setManualForm(prev => ({ ...prev, turf_name: e.target.value }))}
                  placeholder="Turf 1"
                  className="w-full bg-white/10 text-white text-sm rounded-lg px-3 py-2 border border-white/20 focus:border-[#3AAFA9] focus:outline-none placeholder:text-white/30"
                />
              </div>
            </div>

            <button
              onClick={handleCreateManualFixture}
              disabled={loading || !manualForm.team_a_id || !manualForm.team_b_id}
              className="w-full py-2.5 bg-[#3AAFA9] text-white font-bold rounded-lg hover:bg-[#2D8A85] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="create-fixture-btn"
            >
              {loading ? (
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
              ) : (
                <>
                  <i className="ph-bold ph-plus-circle"></i>
                  Create Match
                </>
              )}
            </button>
          </div>
        )}

        {/* Points Tab — read-only when the rulebook sets the points */}
        {activeTab === 'points' && managed && (
          <div className="space-y-3" data-testid="points-from-rulebook">
            <HowPointsWork rules={standingsRules} tone="dark" canEdit testId="admin-how-points-work" />
            <p className="text-xs text-white/60">
              Points, tie-breakers and the knockout format are set in the Rulebook — the only place they live.
              Changes there update the standings straight away.
            </p>
          </div>
        )}
        {activeTab === 'points' && !managed && (
          <div className="space-y-3" data-testid="points-no-rulebook">
            <div className="rounded-xl p-3.5 bg-white/10 text-white">
              <p className="text-sm font-extrabold">Points in use</p>
              <p className="text-xs text-white/85 mt-1" data-testid="points-in-use">
                Win {pointConfig?.points_win ?? 2} · Loss {pointConfig?.points_loss ?? 0} · Tie {pointConfig?.points_tie ?? 1} · No result {pointConfig?.points_no_result ?? 1}
              </p>
            </div>
            <p className="text-xs text-white/60">
              Points, walkovers, tie-breakers and the knockout format are set in the Rulebook.
              Set one up to change them; the standings update straight away.
            </p>
            <button
              onClick={() => { setSearchParams({ phase: 'rules' }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="w-full py-2.5 bg-[#3AAFA9] text-white font-bold rounded-lg flex items-center justify-center gap-2"
              data-testid="points-open-rulebook"
            >
              <i className="ph-bold ph-book-open"></i>
              Set up in Rulebook
            </button>
          </div>
        )}
      </div>

      {/* Add Knockouts Modal */}
      {showAddKnockoutsModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1100] p-4"
          onClick={() => setShowAddKnockoutsModal(false)}
          data-testid="add-knockouts-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md overflow-hidden max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-bold text-lg text-[#2B3A67] flex items-center gap-2">
                  <i className="ph-bold ph-tree-structure"></i>
                  Add Knockout Stages
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Creates knockout fixtures on top of the existing league. Top teams from
                  the current standings are auto-seeded (1 vs N, 2 vs N-1 …). Existing
                  league results are untouched.
                </p>
              </div>
              <button
                onClick={() => setShowAddKnockoutsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 flex-shrink-0"
                aria-label="Close"
                data-testid="add-knockouts-close"
              >×</button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              {knockoutFromRulebook && (
                <p className="text-sm text-[#2B3A67] bg-[#E9EBF1]/60 rounded-xl p-3" data-testid="add-knockouts-rulebook">
                  <span className="font-bold">From the Rulebook.</span> {knockoutLine}
                </p>
              )}
              {!knockoutFromRulebook && (<>
              <label className="text-xs text-gray-500 uppercase tracking-wide block mb-1">
                Which stages?
              </label>
              <div className="flex flex-wrap gap-2">
                {KNOCKOUT_STAGES.map(stage => (
                  <label
                    key={stage.value}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs cursor-pointer transition-colors border ${
                      addKnockoutsStages.includes(stage.value)
                        ? 'bg-[#3AAFA9] text-white border-[#3AAFA9]'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`add-kn-stage-${stage.value}`}
                  >
                    <input
                      type="checkbox"
                      checked={addKnockoutsStages.includes(stage.value)}
                      onChange={e => {
                        if (e.target.checked) setAddKnockoutsStages(prev => [...prev, stage.value]);
                        else setAddKnockoutsStages(prev => prev.filter(s => s !== stage.value));
                      }}
                      className="hidden"
                    />
                    {stage.label}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-2">
                Seeding will use the current <strong>Standings</strong>. For SF + Final,
                the top 4 teams are paired <strong>#1 vs #4</strong> and <strong>#2 vs #3</strong>.
              </p>
              </>)}
            </div>
            <div className="p-5 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setShowAddKnockoutsModal(false)}
                disabled={loading}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50"
                data-testid="add-knockouts-cancel"
              >Cancel</button>
              <button
                onClick={handleAddKnockouts}
                disabled={loading || (!knockoutFromRulebook && addKnockoutsStages.length === 0)}
                className="flex-1 py-2.5 bg-[#3AAFA9] text-white rounded-xl font-bold hover:bg-[#2D8A85] disabled:opacity-50"
                data-testid="add-knockouts-confirm"
              >{loading ? 'Adding…' : 'Add & Seed'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
